// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import addQuoteTocart from "@bg/App/Handlers/CustomQuote/AddCustomQuoteToCart";
import convertToQuote from "@bg/App/Handlers/CustomQuote/ConvertToEpiQuote";
import deleteQuote from "@bg/App/Handlers/CustomQuote/DeleteCustomQuote";
import { CustomQuoteModel } from "@bg/App/Services/CustomQuoteService";
import { linkIconStyleGenerator, pageTitlesStyle } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import MyCustomQuotesEditForm from "@bg/App/Widgets/MyCustomQuotes/MyCustomQuoteEditForm";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { markForFocusLater } from "@insite/mobius/Overlay/helpers/focusManager";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

interface State {
    updateListModalIsOpen: boolean;
    deleteListModalIsOpen: boolean;
}

const mapStateToProps = (state: CustomAppState) => ({
    quote: state.myCustomQuoteDetails.quote as CustomQuoteModel,
    addingToCart: state.myCustomQuoteDetails.addingToCart,
    canRequestQuote: getCurrentCartState(state).value?.canRequestQuote,
    selectedLineIds: state.myCustomQuoteDetails.selectedLineIds,
    myCustomQuotesUrl: getPageLinkByPageType(state, "MyCustomQuotesPage")?.url,
    rfqQuoteDetailsUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    language: state.context.session.language,
    isLoading: state.myCustomQuoteDetails.isLoading,
});

const mapDispatchToProps = {
    addQuoteTocart,
    deleteQuote,
    convertToQuote,
};

type Props = WidgetProps & HasHistory & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListsDetailsActionStyles {
    wrapper?: InjectableCss;
    editLink?: LinkPresentationProps;
    titlWrapper?: InjectableCss;
    modifiedWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    wideHidden?: HiddenProps;
    nameText?: TypographyProps;
    overflowMenu?: OverflowMenuPresentationProps;
    narrowHidden?: HiddenProps;
    editButton?: ButtonPresentationProps;
    requestQuote?: ButtonPresentationProps;
    addItemButton?: ButtonPresentationProps;
    addListButton?: ButtonPresentationProps;
    editListModal?: ModalPresentationProps;
    deleteListModal?: TwoButtonModalStyles;
}

export const actionStyles: MyListsDetailsActionStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin-bottom: 30px;
        `,
    },
    editLink: linkIconStyleGenerator("Create", "13px", 14, "line-height: 13px;"),
    titlWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            max-width: 50%;
        `,
    },
    modifiedWrapper: {
        css: css`
            width: 100%;
            && > * {
                font-size: 13px;
                line-height: 20px;
            }
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            align-items: flex-start;
        `,
    },
    wideHidden: {
        below: "lg",
        css: css`
            display: flex;
            flex-wrap: nowrap;
        `,
    },
    nameText: {
        css: css`
            ${pageTitlesStyle()}
            margin-bottom: 0;
            margin-right: 15px;
        `,
        ellipsis: true,
    },
    narrowHidden: {
        above: "md",
    },
    editButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "secondary",
    },
    addItemButton: {
        typographyProps: {
            weight: "bold",
            css: css`
                white-space: nowrap;
            `,
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "secondary",
    },
    addListButton: {
        css: css`
            margin-right: 15px;
            min-width: 238px;
            &&&,
            &&&:disabled {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                border: 2px solid ${getColor("primary")};
            }
            &&&:hover:not(:disabled) {
                background: ${getColor("common.white")};
                color: ${getColor("primary")};
            }
        `,
    },
    requestQuote: {
        variant: "secondary",
        css: css`
            margin-right: 15px;
        `,
    },
    editListModal: {
        sizeVariant: "small",
    },
};

const styles = actionStyles;

class MyCustomQuoteDetailsHeader extends React.Component<Props, State> {
    static contextType = ToasterContext;
    context!: React.ContextType<typeof ToasterContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            updateListModalIsOpen: false,
            deleteListModalIsOpen: false,
        };
    }

    displayToast(message: React.ReactNode) {
        this.context.addToast({ body: message, messageType: "success" });
    }

    linesSelected() {
        if (this.props.selectedLineIds && this.props.selectedLineIds.length > 0) {
            return true;
        }
        return false;
    }

    editClickHandler = () => {
        this.setState({ updateListModalIsOpen: true });
    };

    editCloseHandler = () => {
        this.setState({ updateListModalIsOpen: false });
    };

    addToCartClickHandler = (e: any) => {
        this.props.addQuoteTocart({
            quoteId: this.props.quote.id,
            onSuccess: this.onAddToCartSuccess,
            onComplete(result: any) {
                if (result.successful) {
                    this.onSuccess?.();
                }
            },
        });
    };

    requestQuoteForCustomQuote = () => {
        const { quote } = this.props;
        if (Object.keys(quote.groupLinesModel).filter(key => quote.groupLinesModel[key].length > 0)?.length > 9) {
            this.context.addToast({ body: siteMessage("Custom_MaxAllowedGroups"), messageType: "danger" });
        }
        this.props.convertToQuote({ quoteId: this.props.quote.id });
    };

    onAddToCartSuccess = (apiResult?: CartLineCollectionModel) => {
        this.displayToast(translate("Quote Added to Cart"));
    };

    deleteClickHandler = () => {
        this.setState({ deleteListModalIsOpen: true });
    };

    deleteCancelHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
    };

    deleteSubmitHandler = () => {
        this.props.deleteQuote({
            quoteId: this.props.quote.id,
            onSuccess: this.onDeleteSuccess,
            onComplete(result: any) {
                if (result.successful) {
                    this.onSuccess?.();
                }
            },
        });
    };

    onDeleteSuccess = () => {
        this.displayToast(translate("Quote Deleted"));
        this.deleteCancelHandler();
        if (this.props.myCustomQuotesUrl) {
            this.props.history.push(this.props.myCustomQuotesUrl);
        }
    };

    getModifiedOnDate = (date: string) => {
        const { language } = this.props;
        const updatedOnDisplay = getLocalizedDateTime({
            dateTime: new Date(date),
            language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
        return updatedOnDisplay;
    };

    returnToAssociatedQuote = () => {
        const { rfqQuoteDetailsUrl, quote } = this.props;
        if (rfqQuoteDetailsUrl && quote.quoteIdReference) {
            window.location.href = `${rfqQuoteDetailsUrl}?quoteId=${quote.quoteIdReference}`;
        }
    };

    render() {
        const { quote, canRequestQuote, addingToCart, isLoading } = this.props;
        if (!quote || isLoading) {
            return null;
        }

        const showAddToCart = quote.linesCount > 0;
        const showRequestQuote = quote.linesCount > 0 && canRequestQuote;
        const addListToCartButtonText =
            addingToCart.length && addingToCart === quote.id ? (
                <LoadingSpinner size={14} />
            ) : (
                translate("Add Quote to Cart")
            );
        const requestQuoteButtonText = translate("Request Quote");
        const ReturnToOriginalQuote = translate("Return to Associated Quote");

        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titlWrapper}>
                    <Typography as="h1" variant="h1" {...styles.nameText} data-test-selector="customQuoteName">
                        {quote.name}
                    </Typography>
                    <Hidden {...styles.wideHidden}>
                        <Link {...styles.editLink} onClick={this.editClickHandler}>
                            {translate("Edit")}
                        </Link>
                    </Hidden>
                </StyledWrapper>
                <StyledWrapper {...styles.buttonWrapper} data-test-selector="menuWrapper">
                    <Hidden {...styles.narrowHidden}>
                        <OverflowMenu position="end" onOpen={markForFocusLater} {...styles.overflowMenu}>
                            <Clickable onClick={this.editClickHandler}>{translate("Edit")}</Clickable>
                            {showAddToCart && !quote.isCameFromNormalQuote ? (
                                <Clickable onClick={this.addToCartClickHandler}>{addListToCartButtonText}</Clickable>
                            ) : null}
                            {quote.isCameFromNormalQuote &&
                            quote.quoteIdReference &&
                            quote.quoteIdReference?.length > 0 &&
                            quote.allowRedirectToAssociatedQuote ? (
                                <Clickable onClick={this.returnToAssociatedQuote}>{ReturnToOriginalQuote}</Clickable>
                            ) : null}
                            {showRequestQuote ? (
                                <Clickable onClick={this.requestQuoteForCustomQuote}>
                                    {requestQuoteButtonText}
                                </Clickable>
                            ) : null}

                            <Clickable onClick={this.deleteClickHandler}>{translate("Delete")}</Clickable>
                        </OverflowMenu>
                    </Hidden>
                    <Hidden {...styles.wideHidden} data-test-selector="wideHidden">
                        <Button
                            {...styles.requestQuote}
                            disabled={!showRequestQuote}
                            data-test-selector="RequestQuoteForCustomQuote"
                            onClick={this.requestQuoteForCustomQuote}
                        >
                            {requestQuoteButtonText}
                        </Button>
                        {!quote.isCameFromNormalQuote && (
                            <Button
                                disabled={!showAddToCart}
                                {...styles.addListButton}
                                onClick={this.addToCartClickHandler}
                                data-test-selector="addCustomQuoteToCart"
                            >
                                {addListToCartButtonText}
                            </Button>
                        )}
                        {quote.isCameFromNormalQuote &&
                            quote.quoteIdReference &&
                            quote.quoteIdReference?.length > 0 &&
                            quote.allowRedirectToAssociatedQuote && (
                                <Button
                                    {...styles.addListButton}
                                    onClick={this.returnToAssociatedQuote}
                                    data-test-selector="returnToAssociatedQuote"
                                >
                                    {ReturnToOriginalQuote}
                                </Button>
                            )}

                        <OverflowMenu {...styles.overflowMenu} onOpen={markForFocusLater}>
                            <Clickable onClick={this.deleteClickHandler} data-test-selector="deleteCustomQuote">
                                {translate("Delete")}
                            </Clickable>
                        </OverflowMenu>
                    </Hidden>
                </StyledWrapper>
                <StyledWrapper {...styles.modifiedWrapper}>
                    <Typography>
                        {new Date().toDateString() === new Date(quote.createdOn).toDateString()
                            ? translate("Created On {0}", this.getModifiedOnDate(quote.createdOn))
                            : translate("Updated On {0}", this.getModifiedOnDate(quote.modifiedOn))}
                    </Typography>
                    &nbsp;
                    <Typography>{translate("By {0}", quote.modifiedBy)}</Typography>
                </StyledWrapper>

                <Modal
                    headline={translate("Edit Quote Detail")}
                    {...styles.editListModal}
                    isOpen={this.state.updateListModalIsOpen}
                    handleClose={this.editCloseHandler}
                >
                    <MyCustomQuotesEditForm
                        quote={quote}
                        onCancel={this.editCloseHandler}
                        onSubmit={this.editCloseHandler}
                    ></MyCustomQuotesEditForm>
                </Modal>
                <TwoButtonModal
                    headlineText={translate("Delete Quote")}
                    {...styles.deleteListModal}
                    modalIsOpen={this.state.deleteListModalIsOpen}
                    messageText={`${translate("Are you sure you want to delete")} ${quote.name}?`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={this.deleteCancelHandler}
                    onSubmit={this.deleteSubmitHandler}
                    submitTestSelector="submitDeleteCustomQuote"
                ></TwoButtonModal>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(MyCustomQuoteDetailsHeader)),
    definition: {
        group: "Custom Quote Details" as any,
        displayName: "Header",
        allowedContexts: ["MyCustomQuoteDetailsPage" as any],
    },
};

export default widgetModule;
